/**
 * Onboarding Funnel
 *
 * Shows per-user progress through key first-value milestones.
 * Data comes from audit_events where event_type matches workflow milestone types.
 * Also wires the key milestone events that weren't previously called:
 *   – First login is tracked here via authTelemetry.
 *   – Subsequent milestones (item shaped, cycle created) are tracked by their
 *     respective stores when they fire.
 *
 * Route: /prioritization/settings/onboarding-funnel
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Circle,
  AlertCircle,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';

// ── Milestone definitions ─────────────────────────────────────────────────────
// Must match workflowTelemetry.ts FIRST_VALUE_WORKFLOW_STEPS and event types.

interface Milestone {
  id: string;
  label: string;
  description: string;
  eventTypes: string[];   // audit_events.event_type values that mark this complete
}

const MILESTONES: Milestone[] = [
  {
    id: 'login',
    label: 'Signed in',
    description: 'User has logged into the workspace at least once',
    eventTypes: ['WorkflowStarted', 'login_success', 'user_login'],
  },
  {
    id: 'idea_submitted',
    label: 'First item submitted',
    description: 'User has submitted or created their first item',
    eventTypes: ['FirstIdeaSubmitted', 'item_created', 'shaping_started'],
  },
  {
    id: 'item_shaped',
    label: 'Item defined',
    description: 'User has defined an item with category and altitude',
    eventTypes: ['FirstItemShaped', 'WorkflowStepCompleted'],
  },
  {
    id: 'session_joined',
    label: 'Session joined',
    description: 'User has joined and participated in a prioritization session',
    eventTypes: ['session_joined', 'SESSION_STARTED', 'WorkflowStepCompleted'],
  },
  {
    id: 'priority_committed',
    label: 'Priority committed',
    description: 'User has seen or acted on a committed priority list',
    eventTypes: ['FirstPriorityCommitted', 'WorkflowCompleted', 'PRIORITY_SET_PUBLISHED'],
  },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface WorkspaceUser {
  id: string;
  name: string;
  email: string;
}

interface UserProgress {
  user: WorkspaceUser;
  milestones: Record<string, string | null>;  // milestoneId → ISO timestamp or null
  completedCount: number;
}

// ── Components ────────────────────────────────────────────────────────────────

function FunnelBar({ count, total, label }: { count: number; total: number; label: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)] w-32 text-right flex-shrink-0">{label}</span>
      <div className="flex-1 bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] rounded-full h-5 overflow-hidden">
        <div
          className="h-full bg-[var(--slate)] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
          style={{ width: `${Math.max(pct, pct > 0 ? 4 : 0)}%` }}
        >
          {pct > 15 && (
            <span className="text-[10px] font-bold text-white">{Math.round(pct)}%</span>
          )}
        </div>
      </div>
      <span className="text-[12px] font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)] w-12 flex-shrink-0">
        {count} / {total}
      </span>
    </div>
  );
}

function UserProgressRow({ progress }: { progress: UserProgress }) {
  return (
    <tr className="border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--active-tint)] flex items-center justify-center flex-shrink-0">
            <span className="text-[11px] font-bold text-[var(--text-sec)]">
              {(progress.user.name || progress.user.email || '?').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {progress.user.name || progress.user.email}
            </div>
            {progress.user.name && (
              <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">{progress.user.email}</div>
            )}
          </div>
        </div>
      </td>
      {MILESTONES.map(m => {
        const ts = progress.milestones[m.id];
        return (
          <td key={m.id} className="px-3 py-3 text-center">
            {ts ? (
              <span title={format(new Date(ts), 'PPpp')}>
                <CheckCircle2 className="h-4 w-4 text-[var(--green)] mx-auto" />
              </span>
            ) : (
              <Circle className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--rule)] mx-auto" />
            )}
          </td>
        );
      })}
      <td className="px-4 py-3 text-right">
        <span className={`text-[12px] font-semibold ${
          progress.completedCount === MILESTONES.length
            ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
            : progress.completedCount === 0
            ? 'text-[var(--text-ter)]'
            : 'text-[var(--mustard)]'
        }`}>
          {progress.completedCount} / {MILESTONES.length}
        </span>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OnboardingFunnel() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);

  const workspaceId = getCurrentWorkspaceId();

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    setIsLoading(true);
    setError(null);

    // 1. Load all workspace users
    const { data: members, error: memberErr } = await supabase
      .from('users')
      .select('id, name, email')
      .in(
        'id',
        // subquery: users who are members of this workspace
        (await supabase
          .from('workspace_members')
          .select('user_id')
          .eq('workspace_id', workspaceId)
        ).data?.map((m: { user_id: string }) => m.user_id) ?? []
      );

    if (memberErr) {
      // Fallback: just get all users if workspace_members query fails
    }

    // 2. Load all milestone event types from audit_events for this workspace
    const allEventTypes = MILESTONES.flatMap(m => m.eventTypes);
    const { data: auditRows, error: auditErr } = await supabase
      .from('audit_events')
      .select('event_type, created_by, created_at')
      .eq('workspace_id', workspaceId)
      .in('event_type', allEventTypes)
      .order('created_at', { ascending: true });

    if (auditErr) { setError(auditErr.message); setIsLoading(false); return; }

    // 3. Also check governance_events for session participation milestones
    const govEventTypes = ['SESSION_STARTED', 'PRIORITY_SET_PUBLISHED'];
    const { data: govRows } = await supabase
      .from('governance_events')
      .select('event_type, actor_id, actor_name, occurred_at')
      .eq('workspace_id', workspaceId)
      .in('event_type', govEventTypes)
      .order('occurred_at', { ascending: true });

    // Build set of actor IDs seen in events
    const actorIds = new Set<string>();
    (auditRows ?? []).forEach((r: { created_by: string }) => { if (r.created_by) actorIds.add(r.created_by); });
    (govRows ?? []).forEach((r: { actor_id: string | null }) => { if (r.actor_id) actorIds.add(r.actor_id); });

    // Targeted lookup: fetch users by the exact IDs we saw in events
    const actorIdList = Array.from(actorIds);
    const { data: actorUsers } = actorIdList.length > 0
      ? await supabase.from('users').select('id, name, email').in('id', actorIdList)
      : { data: [] as { id: string; name: string | null; email: string | null }[] };

    // Build user display map — prefer targeted lookup, fall back to workspace members / wsUsers
    const userMap = new Map<string, WorkspaceUser>();
    const allKnownUsers = [
      ...(actorUsers ?? []),
      ...(members ?? []),
    ] as { id: string; name: string | null; email: string | null }[];
    allKnownUsers.forEach(u => {
      if (!userMap.has(u.id) && actorIds.has(u.id)) {
        userMap.set(u.id, { id: u.id, name: u.name ?? '', email: u.email ?? '' });
      }
    });
    // Fallback for any actors still not in users table
    actorIds.forEach(id => {
      if (!userMap.has(id)) {
        userMap.set(id, { id, name: `User ${id.slice(0, 8)}`, email: '' });
      }
    });

    // 5. Per-user earliest timestamp for each milestone
    const milestoneMap = new Map<string, Map<string, string>>(); // userId → milestoneId → timestamp
    userMap.forEach((_, uid) => milestoneMap.set(uid, new Map()));

    // Audit events
    (auditRows ?? []).forEach((row: { event_type: string; created_by: string | null; created_at: string }) => {
      const uid = row.created_by;
      if (!uid || !userMap.has(uid)) return;
      MILESTONES.forEach(m => {
        if (m.eventTypes.includes(row.event_type)) {
          const uMap = milestoneMap.get(uid)!;
          const existing = uMap.get(m.id);
          if (!existing || row.created_at < existing) uMap.set(m.id, row.created_at);
        }
      });
    });

    // Governance events (session_joined milestone)
    (govRows ?? []).forEach((row: { event_type: string; actor_id: string | null; occurred_at: string }) => {
      const uid = row.actor_id;
      if (!uid || !userMap.has(uid)) return;
      MILESTONES.forEach(m => {
        if (m.eventTypes.includes(row.event_type)) {
          const uMap = milestoneMap.get(uid)!;
          const existing = uMap.get(m.id);
          if (!existing || row.occurred_at < existing) uMap.set(m.id, row.occurred_at);
        }
      });
    });

    // 6. Build UserProgress array
    const progress: UserProgress[] = Array.from(userMap.values()).map(user => {
      const uMap = milestoneMap.get(user.id) ?? new Map();
      const milestones: Record<string, string | null> = {};
      MILESTONES.forEach(m => { milestones[m.id] = uMap.get(m.id) ?? null; });
      return {
        user,
        milestones,
        completedCount: Object.values(milestones).filter(Boolean).length,
      };
    });

    // Sort by completion count desc
    progress.sort((a, b) => b.completedCount - a.completedCount);

    setUserProgress(progress);
    setIsLoading(false);
  }, [workspaceId]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  // Funnel counts
  const funnelCounts = useMemo(() => {
    return MILESTONES.map(m => ({
      milestone: m,
      count: userProgress.filter(u => u.milestones[m.id] !== null).length,
    }));
  }, [userProgress]);

  const totalUsers = userProgress.length;
  const fullyOnboarded = userProgress.filter(u => u.completedCount === MILESTONES.length).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<TrendingUp className="h-4 w-4 text-[var(--green)]" />}
        iconBg="bg-[var(--green-dim)]"
        title="Onboarding Funnel"
        subtitle="Track which first-value milestones each user has completed — see where teammates drop off"
        actions={
          <button
            onClick={() => void fetchData()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4">
          <Users className="h-5 w-5 text-[var(--text-ter)] mb-2" />
          <div className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{totalUsers}</div>
          <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">Active users tracked</div>
        </div>
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4">
          <CheckCircle2 className="h-5 w-5 text-[var(--green)] mb-2" />
          <div className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{fullyOnboarded}</div>
          <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">Fully onboarded</div>
        </div>
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-4">
          <TrendingUp className="h-5 w-5 text-[var(--mustard)] mb-2" />
          <div className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {totalUsers > 0 ? Math.round((fullyOnboarded / totalUsers) * 100) : 0}%
          </div>
          <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">Completion rate</div>
        </div>
      </div>

      {/* Funnel visualization */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-4">Milestone Funnel</h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-[var(--text-ter)] py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : (
          <div className="space-y-3">
            {funnelCounts.map(({ milestone, count }) => (
              <FunnelBar
                key={milestone.id}
                count={count}
                total={totalUsers}
                label={milestone.label}
              />
            ))}
          </div>
        )}
        {!isLoading && totalUsers === 0 && (
          <div className="text-center py-8 text-[var(--text-ter)]">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No milestone data yet.</p>
            <p className="text-[12px] mt-1">Milestones are recorded as users take first actions in the workspace.</p>
          </div>
        )}
      </div>

      {/* Per-user table */}
      {!isLoading && userProgress.length > 0 && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/60 dark:bg-[var(--surface-raised)]">
            <span className="text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider">Per-user breakdown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--rule)] dark:border-[var(--rule)]">
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider">User</th>
                  {MILESTONES.map(m => (
                    <th key={m.id} className="px-3 py-2.5 text-center text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider" title={m.description}>
                      {m.label}
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider">Done</th>
                </tr>
              </thead>
              <tbody>
                {userProgress.map(p => <UserProgressRow key={p.user.id} progress={p} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}
